/**
 * Sentry and PostHog for the portal.
 *
 * Sentry is inert when VITE_SENTRY_DSN is absent. PostHog loads only on
 * portal.axiohire.com when the project key is present. Never logs PII
 * (email, phone, tokens, form values, resumes, Authorization headers).
 * Query strings and hashes are stripped from URL properties before sending.
 */

import * as Sentry from "@sentry/react";
import type { CaptureResult, Properties } from "posthog-js";
import { ADMIN_PATH, PORTAL_ORIGIN } from "../constants/publicUrls";

export type ObservabilityContext = Record<string, string | number | boolean | null | undefined>;

const PRODUCTION_HOST = new URL(PORTAL_ORIGIN).hostname;
const ANALYTICS_SOURCE = "portal";
const ADMIN_ROLE = "admin";
const POSTHOG_KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim() ?? "";
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() ?? "";

type PostHogClient = typeof import("posthog-js").default;
let analyticsClient: Promise<PostHogClient | null> | null = null;

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim() ?? "";
const ENVIRONMENT =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)?.trim() ||
  (import.meta.env.MODE as string | undefined) ||
  "development";
const RELEASE = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim() || "local";

let initialized = false;

const SENSITIVE_KEY =
  /^(email|phone|password|token|authorization|cookie|otp|code|resume|body|query|file|ssn|pan|aadhaar)$/i;

const URL_PROPERTY_KEYS = [
  "$current_url",
  "$referrer",
  "$initial_referrer",
  "$initial_current_url",
  "$pathname",
] as const;

const SENSITIVE_EVENT_PROPERTIES = ["id_token", "code", "state", "user", "q"] as const;

/** Drop search and hash so filter text and Apple tokens never leave the browser. */
export function stripUrlQuery(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    const parsed = new URL(value, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    const cut = value.search(/[?#]/);
    return cut === -1 ? value : value.slice(0, cut);
  }
}

function scrubUrlRecord(record: Properties | undefined): void {
  if (!record) return;
  for (const key of URL_PROPERTY_KEYS) {
    if (record[key] == null) continue;
    record[key] = stripUrlQuery(record[key]);
  }
}

function dropSensitiveEventProperties(record: Properties | undefined): void {
  if (!record) return;
  for (const key of SENSITIVE_EVENT_PROPERTIES) {
    delete record[key];
  }
}

function scrubAnalyticsEvent(event: CaptureResult): CaptureResult {
  scrubUrlRecord(event.properties);
  scrubUrlRecord(event.$set);
  scrubUrlRecord(event.$set_once);
  dropSensitiveEventProperties(event.properties);
  return event;
}

function sanitizeContext(ctx?: ObservabilityContext): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!ctx) return out;
  for (const [key, value] of Object.entries(ctx)) {
    if (value == null) continue;
    if (SENSITIVE_KEY.test(key)) continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      out[key] = value;
    }
  }
  return out;
}

function isAdminPath(pathname: string): boolean {
  return pathname === ADMIN_PATH || pathname.startsWith(`${ADMIN_PATH}/`);
}

function analyticsEnabled(): boolean {
  return typeof window !== "undefined" && window.location.hostname === PRODUCTION_HOST && Boolean(POSTHOG_KEY && POSTHOG_HOST);
}

function loadAnalytics(): Promise<PostHogClient | null> {
  if (!analyticsEnabled()) return Promise.resolve(null);
  analyticsClient ??= import("posthog-js").then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: "history_change",
      capture_pageleave: true,
      autocapture: false,
      capture_exceptions: false,
      disable_session_recording: true,
      cross_subdomain_cookie: true,
      person_profiles: "identified_only",
      before_send: (event) => {
        if (event == null || isAdminPath(window.location.pathname)) return null;
        return scrubAnalyticsEvent(event);
      },
    });
    posthog.register({ source: ANALYTICS_SOURCE });
    if (isAdminPath(window.location.pathname)) posthog.opt_out_capturing();
    return posthog;
  });
  return analyticsClient;
}

/** Call once before React renders. Safe no-op without DSN or outside production. */
export function initObservability(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  void loadAnalytics();
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    ignoreErrors: [
      "ResizeObserver loop",
      "Non-Error promise rejection captured",
      /Loading chunk [\d]+ failed/i,
    ],
  });
}

export function identifyUser(userId: string, role: string): void {
  if (userId && DSN) {
    Sentry.setUser({ id: userId });
    Sentry.setTag("role", role);
  }
  if (!userId) return;
  void loadAnalytics().then((posthog) => {
    if (!posthog) return;
    if (role === ADMIN_ROLE) {
      posthog.opt_out_capturing();
      return;
    }
    posthog.identify(userId, { role });
  });
}

export function clearUser(): void {
  if (DSN) Sentry.setUser(null);
  if (!analyticsClient) return;
  void analyticsClient.then((posthog) => {
    if (!posthog) return;
    posthog.reset();
    posthog.opt_in_capturing();
  });
}

export function trackEvent(name: string, context?: ObservabilityContext): void {
  const data = sanitizeContext(context);
  if (DSN) {
    Sentry.addBreadcrumb({
      category: "app",
      message: name,
      level: "info",
      data,
    });
  }
  void loadAnalytics().then((posthog) => {
    posthog?.capture(name, data);
  });
}

export function trackWarning(message: string, context?: ObservabilityContext): void {
  if (!DSN) return;
  Sentry.captureMessage(message, {
    level: "warning",
    extra: sanitizeContext(context),
  });
}

export function trackError(message: string, context?: ObservabilityContext): void {
  if (!DSN) return;
  Sentry.captureMessage(message, {
    level: "error",
    extra: sanitizeContext(context),
  });
}

export function captureException(error: unknown, context?: ObservabilityContext): void {
  if (!DSN) return;
  const err = error instanceof Error ? error : new Error(String(error));
  Sentry.withScope((scope) => {
    const safe = sanitizeContext(context);
    for (const [k, v] of Object.entries(safe)) {
      scope.setExtra(k, v);
    }
    if (typeof window !== "undefined") {
      scope.setTag("route", window.location.pathname);
    }
    Sentry.captureException(err);
  });
}

/** Bucket file sizes so we never log exact resume byte counts as identifiers. */
export function fileSizeBucket(bytes: number): string {
  if (bytes < 100_000) return "<100kb";
  if (bytes < 500_000) return "100-500kb";
  if (bytes < 1_000_000) return "500kb-1mb";
  if (bytes < 3_000_000) return "1-3mb";
  return "3mb+";
}

export function resumeKindFromName(name: string): "pdf" | "docx" | "other" {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".docx")) return "docx";
  return "other";
}

export function flushObservability(): Promise<void> {
  if (!DSN) return Promise.resolve();
  return Sentry.flush(2000).then(() => undefined);
}

export const observabilityEnabled = {
  errors: Boolean(DSN),
};
